import { isPasskeySupported } from 'app/auth/passkeyClient';
import { shouldShowPasskeyPrompt } from 'app/auth/passkeyPromptChoice';

/**
 * Session-scoped (per browser tab session) guard so the SEE-213 passkey prompt
 * shows at most once per login session per user. Keyed by Cognito sub so a
 * second user signing in on the same tab still gets their own prompt.
 * PasskeyPrompt writes it on mount; Landing reads it before redirecting.
 */
export function passkeyPromptShownKeyFor(sub: string): string {
  return `passkeyPromptShown:${sub}`;
}

export function wasPasskeyPromptShownThisSession(sub: string): boolean {
  try {
    return sessionStorage.getItem(passkeyPromptShownKeyFor(sub)) === '1';
  } catch {
    // Storage unavailable: claim "shown" so Landing never enters a redirect
    // loop it cannot break out of.
    return true;
  }
}

/**
 * SEE-226: allow the prompt to show again in this tab session. Called when a
 * removal leaves the user with no passkeys, so deleting one and signing back in
 * without opening a new tab still re-offers enrolment.
 */
export function clearPasskeyPromptShownThisSession(sub: string): void {
  try {
    sessionStorage.removeItem(passkeyPromptShownKeyFor(sub));
  } catch {
    /* storage unavailable — nothing to clear */
  }
}

/**
 * Whether the post-login passkey prompt should show for this user: browser
 * supports WebAuthn, no passkey created nor prompt dismissed (per-device), not an
 * IdP-linked (SSO) account — a passkey would bypass the IdP and could never be
 * used at login anyway — and not already shown this tab session. Shared by the
 * post-login redirect (Login) and the fallback check on /landing so the two
 * can never disagree.
 */
export function isEligibleForPasskeyPrompt(
  user: { sub?: string; identities?: string } | undefined
): user is { sub: string } {
  return Boolean(
    user?.sub &&
      !user.identities &&
      isPasskeySupported() &&
      shouldShowPasskeyPrompt(user.sub) &&
      !wasPasskeyPromptShownThisSession(user.sub)
  );
}
